import { createElement, useId, useState, type ReactElement } from "react";
import { Button, Feedback, FormField, Input } from "@zzk/ui";
import { AuthFrame } from "@zzk/admin-pages";
import { brandConfig } from "../../../brand/index.js";

export interface EmailRequestFormProps {
  readonly turnstileSiteKey: string;
  readonly devTurnstileToken?: string;
  readonly action?: string;
  readonly title?: string;
  readonly description?: string;
}

interface SubmitState {
  readonly kind: "idle" | "submitting" | "error";
  readonly message?: string;
}

const authBrand = {
  name: brandConfig.name,
  logoSrc: brandConfig.assets.logo.src,
  logoAlt: brandConfig.assets.logo.alt,
};

export function EmailRequestForm(props: EmailRequestFormProps): ReactElement {
  const emailId = useId();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<SubmitState>({ kind: "idle" });

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email) return;
    setState({ kind: "submitting" });
    try {
      const response = await fetch("/api/lens/auth/code/send", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as {
          error?: { kind?: string };
        };
        setState({
          kind: "error",
          message: friendlyError(body.error?.kind, response.status),
        });
        return;
      }
      const body = (await response.json()) as {
        data: { flow_id: string; expires_in_seconds: number };
      };
      window.location.href = `/app/login/code?flow_id=${encodeURIComponent(
        body.data.flow_id,
      )}`;
    } catch {
      setState({
        kind: "error",
        message: "Não foi possível solicitar o código. Tente novamente.",
      });
    }
  }

  return AuthFrame({
    title: props.title ?? "Entrar no Zzk Lens",
    brand: authBrand,
    description:
      props.description ??
      "Informe o email do operador. Você receberá um código de 6 dígitos.",
    status:
      state.kind === "error"
        ? Feedback({
            type: "error",
            title: "Não foi possível continuar",
            message: state.message ?? "Tente novamente.",
          })
        : null,
    children: createElement(
      "form",
      {
        onSubmit,
        "data-testid": "email-request-form",
        noValidate: true,
        className: "grid gap-4",
      },
      FormField({
        id: emailId,
        name: "email",
        label: "Email do operador",
        required: true,
        control: Input({
          id: emailId,
          name: "email",
          type: "email",
          autoComplete: "email",
          placeholder: "operator@4p1.io",
          value: email,
          onChange: (event) =>
            setEmail((event.target as HTMLInputElement).value),
          required: true,
          disabled: state.kind === "submitting",
        }),
      }),
      createElement(
        Button,
        {
          type: "submit",
          variant: "primary",
          loading: state.kind === "submitting",
          loadingLabel: "Enviando…",
          disabled: state.kind === "submitting" || !email,
          key: "submit",
        },
        "Enviar código",
      ),
    ),
  });
}

function friendlyError(kind: string | undefined, status: number): string {
  if (kind === "rate_limited" || status === 429) {
    return "Muitas tentativas. Aguarde alguns minutos e tente novamente.";
  }
  if (kind === "upstream_unavailable" || status === 503) {
    return "Serviço de autenticação indisponível no momento.";
  }
  return "Não foi possível solicitar o código. Verifique os dados.";
}
